
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import Router, { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildQueryStringFromFilters } from '@unreserved-frontend-v2/modules/listings/utils/routing/build-query-string-from-filters';
import { FILTER_CHANGE_DEBOUNCE, FIRST_PAGE } from '@unreserved-frontend-v2/modules/listings/utils/routing/constants';
import { getFilterSelectionsFromQuery } from '@unreserved-frontend-v2/modules/listings/utils/routing/get-filter-selections-from-query';
import { getQueryConnectionValuesFromInputs } from '@unreserved-frontend-v2/modules/listings/utils/routing/get-query-connection-values-from-inputs';
import { getQueryInputValuesFromFilterSelections } from '@unreserved-frontend-v2/modules/listings/utils/routing/get-query-input-values-from-filter-selections';
import { getRouteSplitQueryParams } from '@unreserved-frontend-v2/modules/listings/utils/routing/get-route-split-query-params';
import { usePaginationUrls } from '@unreserved-frontend-v2/ui/hooks/use-pagination-urls';
import { FilterChangeEvent, FilterSelections } from '@unreserved-frontend-v2/ui/schema-table/models';
import { ValueHash } from '@unreserved-frontend-v2/utils/types';
/**
 * This hook is for processing anything related to connection input changes based off of router query param updates/values
 *
 * @param pageSize How many results per page for pagination calculations
 * @param defaultPrimaryFilters The main filters to use by default on this page
 * @param defaultSecondaryFilters The secondary filters to use by default on this page
 * @param filterChangeDebounce The debounce time between filter changing to batch changes (mainly for mobile)
 */
const useListingsQueryInputValues = (pageSize: number, defaultPrimaryFilters: Record<string, unknown>, defaultSecondaryFilters: Record<string, unknown>, filterChangeDebounce?: number) => {
    /**
     * A fresh load of this hook will always have places/facets requested, as the query params will be considered pristine
     * subsequent logic that changes the filters turn off places/facets in following requests as they are only needed at the start
     *
     * Changes to the slug count as a new page view and remounts the hook so new places/facets will properly be requested again
     **/
    const lastQueryParamsUpdate = useRef<number | undefined>();
    const { query: rawQuery, pathname } = useRouter();
    const { query, routeParams: { listing: slug }, }: {
        query: ParsedUrlQuery;
        routeParams: {
            listing?: string;
        };
    } = useMemo(() => getRouteSplitQueryParams(rawQuery), [rawQuery]);
    const { pageNumber, prevPageUrl, nextPageUrl } = usePaginationUrls(slug || '', query, FIRST_PAGE);
    const onPageChangedCallback = useCallback(() => {
        lastQueryParamsUpdate.current = new Date().getTime();
    }, []);
    const [filterSelections, setFilterSelections] = useState<FilterSelections>(getFilterSelectionsFromQuery(query as ValueHash<string>, slug, defaultPrimaryFilters, defaultSecondaryFilters) || {});
    const variables = useMemo(() => {
        return getQueryConnectionValuesFromInputs(getQueryInputValuesFromFilterSelections(filterSelections), pageNumber, pageSize, filterSelections);
    }, [filterSelections, pageNumber, pageSize]);
    // Treat slug changes the same as a dirty params update
    useEffect(() => {
        if (slug) {
            lastQueryParamsUpdate.current = new Date().getTime();
        }
    }, [slug]);
    useEffect(() => {
        setFilterSelections((existingSelecions) => {
            const newSelections = getFilterSelectionsFromQuery(query as ValueHash<string>, slug, defaultPrimaryFilters, defaultSecondaryFilters);
            return isEqual(newSelections, existingSelecions) ? existingSelecions : newSelections;
        });
    }, [query, slug, setFilterSelections, defaultPrimaryFilters, defaultSecondaryFilters]);
    const handleFilterChange = useMemo(() => {
        return debounce((filterEvent: FilterChangeEvent | FilterChangeEvent[]) => {
            let newFilterSelections = { ...filterSelections };
            const eventsToProcess = [filterEvent].flat();
            eventsToProcess.forEach((event) => {
                newFilterSelections = { ...newFilterSelections, [event.key]: event.value };
            });
            const queryString = buildQueryStringFromFilters(newFilterSelections, pathname, !slug);
            Router.push(queryString, undefined, { shallow: true, scroll: true });
            lastQueryParamsUpdate.current = new Date().getTime();
        }, filterChangeDebounce || FILTER_CHANGE_DEBOUNCE);
    }, [filterChangeDebounce, filterSelections, pathname, slug]);
    useEffect(() => {
        return () => handleFilterChange.cancel();
    }, [handleFilterChange]);
    const clearAllFilters = useCallback(() => {
        const queryObject = {
            ...getFilterSelectionsFromQuery({}, slug)
        };
        // Deleting any unncessary slug-found filters
        delete queryObject['placeSlugs'];
        delete queryObject['residentialTypes'];
        Router.push(buildQueryStringFromFilters(queryObject, pathname, !slug), undefined, { scroll: false });
    }, [pathname, slug]);
    const clearSecondaryFilters = useCallback(() => {
        const queryObject = {
            ...getFilterSelectionsFromQuery(query, slug),
            // Have to use the defaults to overwrite the above so that the filter values are reset.
            ...defaultPrimaryFilters,
            ...defaultSecondaryFilters
        };
        Router.push(buildQueryStringFromFilters(queryObject, pathname, !slug), undefined, { scroll: false });
    }, [defaultPrimaryFilters, defaultSecondaryFilters, pathname, query, slug]);
    return useMemo(() => ({
        prevPageUrl,
        nextPageUrl,
        onPageChangedCallback,
        isQueryDirty: !!lastQueryParamsUpdate.current,
        handleFilterChange,
        filterSelections,
        defaultFilterSelections: defaultPrimaryFilters,
        defaultSecondaryFilterSelections: defaultSecondaryFilters,
        pageNumber,
        clearAllFilters,
        clearSecondaryFilters,
        connectionQueryVariables: variables
    }), [
        prevPageUrl,
        nextPageUrl,
        onPageChangedCallback,
        handleFilterChange,
        filterSelections,
        defaultPrimaryFilters,
        defaultSecondaryFilters,
        pageNumber,
        clearAllFilters,
        clearSecondaryFilters,
        variables,
    ]);
};
export default useListingsQueryInputValues;

    async function __Next_Translate__getServerSideProps__1955d79dffa__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: 'components//[listing]/use-listings-query-input-values',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1955d79dffa__ as getServerSideProps }
  